// Types for file sources types
export enum FileType {
  Video = 'FILE_TYPE_VIDEO',
  Image = 'FILE_TYPE_IMAGE',
}

export enum FileSourceType {
  Vod = 'FILE_SOURCE_ADD_VOD',
  Aod = 'FILE_SOURCE_ADD_AOD',
}

// Interface for the parts of the file being uploaded.
export interface Part {
  etag: string
  part_number: number
}

// Interface for the response received upon completing the file upload
export interface CompleteUploadFileResponse {
  file: FileResponse
}

// Interface for the response containing file details
export interface FileResponse {
  id: string
  uri: string
  name: string
}

// Interface for the data sent to initiate an upload
export interface UploadFileData {
  file: {
    name: string
    size: string
    type: FileType
    source: FileSourceType
    attrs?: Record<string, unknown>
  }
}

// Interface for each part of the file that needs to be uploaded
export interface UploadPart {
  part_number: number
  presigned_url: string
}

// Interface for the response received upon initiating the file upload
export interface UploadFileResponse {
  file: UploadFileData & {
    id: string
    uri: string
  }
  upload_data: {
    id: string
    parts: UploadPart[]
  }
}

export interface GetProfilesResponse {
  pagination: {
    total_items: number
    items_per_page: number
    current_page: number
  }
  profiles: {
    id: string
    name: string
    video_codec: string
    video_width: number
    video_height: number
    video_bitrate: string
    audio_bitrate: string
    codec_profile: string
    level: string
    b_frame: boolean
    frame_rate: string
    gop_size: number
    segment_duration: number
    preset: boolean
    standard: string
  }[]
}

type Profile = {
  id: string
  name: string
  video_codec: string
  video_width: number
  video_height: number
  video_bitrate: string
  audio_bitrate: string
  codec_profile: string
  level: string
  b_frame: boolean
  frame_rate: string
  gop_size: number
  segment_duration: number
  preset: boolean
  standard: string
}

export type ProfileSet = {
  id: string
  name: string
  preset: boolean
  cmaf: boolean
  protocols: string[]
  smart_abr: boolean
  multiple_audio_track: {
    lang_from: string
    lang_customizes: string[]
  }
  video_codec: string
  profiles: Profile[]
}

export interface GetProfileSetsResponse {
  pagination: {
    total_items: number
    items_per_page: number
    current_page: number
  }
  profile_sets: ProfileSet[]
}

export type Vod = {
  id: string
  name: string
  source: {
    type: string
    library: {
      video: {
        id: string
      }
      subtitles: string[]
      stt_subtitles: string[]
    }
    cloud_storage: null
  }
  profile_set: {
    id: string
    name: string
    protocols: string[]
    smart_abr: boolean
    multiple_audio_track: {
      lang_from: string
      lang_customizes: string[]
    }
    video_codec: string
    profiles: Profile[]
    preset: boolean
    cmaf: boolean
  }
  queue: string
  pte: {
    profile: string
  }
  stream: string[]
  labels: string[]
  created_at: string
  status: string
  previous_status: string
  metadata: {
    short_description: string
    long_description: string
    labels: string[]
  }
  source_file_info: {
    file_name: string
    size: string
    bitrate: string
    duration: number
    video: {
      codec: string
      height: number
      width: number
      bitrate: string
      frame_rate: string
      interlace: boolean
      aspect_ratio: string
    }
    audios: {
      codec: string
      bitrate: string
      sample_rate: string
      channels: number
    }[]
  }
  updated_at: string
  schedule: {
    enable: boolean
    start_time: string | null
    end_time: string | null
  }
  security: {
    privacy: {
      type: string
      token: string | null
    }
    watermark: {
      enabled: boolean
      type: string
      position: string
      size: number
      offset: string | null
    }
    domain_control: {
      enabled: boolean
      domains: string[]
    }
    protection: {
      type: string
      drm_config: {
        widevine_config: {
          security_level: string
          token_type: string
        }
      }
    }
    geo_control: string[]
  }
  cover_image: {
    type: string
    auto: {
      url: string
      name: string
      size: string
    }
    updated_at: string
  } | null
  export: string | null
  byo_cdn: string | null
  player_setting: string | null
  chapter_setting: {
    enable: boolean
    chapters: string[]
  }
  subtitles: string[]
  type: string
  org_id: string
  stream_id: string
  summary: {
    content: string
    status: string
    library_id: string
    external_id: string
    character_count: string
    token_count: string
    subtitle_id: string
    detailed_data: string | null
    error_infos: string[]
  }
  permission: {
    default_access_role: string
    assignments: {
      id: string
      email: string
      name: string
      assigned_type: string
      access_role: string
    }[]
  }
  latest_vod_encoding_stages: string[]
  tagging_setting: {
    panel_display: boolean
    seek_bar_display: boolean
  }
  mode: string
}

export interface CreateVodResponse {
  vod: Vod
}

export interface CreatePlaybackTokenResponse {
  token: string
}

type Pagination = {
  total_items: number
  items_per_page: number
  current_page: number
}

export interface GetVodsResponse {
  vods: Vod[]
  pagination: Pagination
}

export interface GetUploadedFilesResponse {
  files: FileResponse[]
  pagination: Pagination
}

export interface GetVodResponse {
  vod: Vod
}

export interface GetVodResponseError {
  code: number
  message: string
  details: {
    reason: string
    domain: string
    metadata: Record<string, unknown>
  }[]
}

export interface DownloadFileResponse {
  id: string
  download_uri: string
  download_filename: string
  expiration_time: string
}

export type ResourceType =
  | 'RESOURCE_TYPE_LIVE'
  | 'RESOURCE_TYPE_VOD'
  | 'RESOURCE_TYPE_LIVE_EVENT'
  | 'RESOURCE_TYPE_LIVE_EVENT_RECORDING'
  | 'RESOURCE_TYPE_VOD_EVENT'
  | 'RESOURCE_TYPE_AUDIO_EVENT'
  | 'RESOURCE_TYPE_VOD_ENCODING'
